import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateCourse } from '@/hooks/useCourses'
import { CourseForm } from '@/components/app/courses/CourseForm'
import { paths } from '@/routes/path'
import { CourseMutationPayload } from '@/services/coursesService'
import BackButton from '@/components/ui/BackButton'

export function CourseCreatePage() {
  const navigate = useNavigate()
  const createCourseMutation = useCreateCourse()
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (payload: CourseMutationPayload) => {
    setError(null)
    try {
      const course = await createCourseMutation.mutateAsync(payload)
      if (course?.id) {
        navigate(paths.app.courses.manage.build(course.id), { replace: true })
      } else {
        navigate(paths.app.courses.root.absolute, { replace: true })
      }
    } catch (err) {
      setError((err as Error).message || 'Unable to create course.')
    }
  }

  return (
    <div className="space-y-6 p-6">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <BackButton />
          <h1 className="text-3xl font-bold text-secondary-900">New Course</h1>
        </div>
        <p className="text-secondary-500">
          Configure the course details and publish when you are ready.
        </p>
        {error && (
          <div className="rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-600">
            {error}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <CourseForm
          submitLabel={createCourseMutation.isPending ? 'Creating…' : 'Create Course'}
          onSubmit={handleSubmit}
          isSubmitting={createCourseMutation.isPending}
        />
        <div className="rounded-md border border-secondary-200 bg-secondary-50 p-6 text-secondary-600">
          <h2 className="text-lg font-semibold text-secondary-900">Add modules after saving</h2>
          <p className="mt-2 text-sm leading-relaxed">
            Once the course is created, you can organise modules and upload their resources from the dedicated modules workspace.
            After creation, open the course from the library and use the Manage Modules option.
          </p>
        </div>
      </div>
    </div>
  )
}
